import * as util from '../../util';
import * as revisions from './revision';

export declare interface MarkerOptions {
    markerNumber: number;
    revision: revisions.Revision;
    committed?: boolean;
}

export declare interface Point {
    x: number;
    y: number;
}

export declare interface StoredMarker {
    position: Point;
    notes: string;
}

declare interface storage {
    markers: StoredMarker[];
}

export class Marker {
    private markerNumber: number;
    private x: number = 0;
    private y: number = 0;
    private notes: string = '';

    UserEventID?: string;

    private markerSize: number = util.RemToPx(1);

    private originalImageHeight: number;
    private originalImageWidth: number;

    private revision: revisions.Revision;

    Element: HTMLElement;

    Committed: boolean;

    NotesEl?: HTMLElement;

    constructor(options: MarkerOptions) {
        this.markerNumber = options.markerNumber;
        this.Committed = options.committed || false;
        this.revision = options.revision;

        this.Element = document.createElement('div') as HTMLElement;
        this.Element.textContent = this.markerNumber.toString();
        this.Element.classList.add('marker');
        this.Element.id = `marker-${this.markerNumber}`;

        this.revision.Element.appendChild(this.Element);

        this.originalImageHeight = this.revision.Proof.naturalHeight;
        this.originalImageWidth = this.revision.Proof.naturalWidth;

        window.addEventListener('resize', () => this.draw());
        this.Element.addEventListener('mousedown', e => !this.Committed && this.down(e));
        this.Element.addEventListener('touchstart', e => !this.Committed && this.down(e));
        this.Element.addEventListener('mouseenter', () => {
            if (!this.Committed) {
                this.Element.style.cursor = 'grab';
            } else {
                this.Element.style.cursor = 'default';
            }
        });
    }

    SetPoint(p: Point) {
        this.x = p.x;
        this.y = p.y;
        this.draw();
    }

    private draw() {
        // check if the image has loaded
        // try and draw again a little later if not
        if (this.originalImageWidth === 0) {
            this.originalImageWidth = this.revision.Proof.naturalWidth;
            this.originalImageHeight = this.revision.Proof.naturalHeight;
            setTimeout(() => this.draw(), 15);
        }

        this.Element.textContent = this.markerNumber.toString();
        this.Element.style.top = `${this.y * (this.revision.Proof.clientHeight / this.originalImageHeight)}px`;
        this.Element.style.left = `${this.x * (this.revision.Proof.clientWidth / this.originalImageWidth)}px`;
    }

    GetPoint(): Point {
        return {
            x: this.x,
            y: this.y,
        };
    }

    SetNotes(t: string) {
        this.notes = t;
    }

    GetNotes(): string {
        return this.notes;
    }

    Renumber(n: number) {
        if (n < 1) {
            return;
        }

        this.markerNumber = n;
        this.draw();
    }

    Remove() {
        this.Element.remove();
    }

    GetMarker(): StoredMarker {
        return {
            position: {
                x: this.x,
                y: this.y,
            },
            notes: this.notes,
        };
    }

    // yes, we do actually have to handle both mouse and
    // touch events.
    // touch events are fired from mouse events, but touch
    // events store their position data by the number of touches
    // to be able to handle multiple touches and are not stored at the
    // same variable as mouse events. Why they did this, no idea. They could just
    // do this by using the same variable and just taking the first touch like
    // implemented below.
    //
    // typescript also needs to know which kind of event is happened
    // otherwise it has a huge hissyfit so both the mouse and touch
    // events need to be defined in order for this to work
    //
    // I have tested moving the markers with touches on a mobile browser
    // and it does not work unless i do it this way.
    //
    // for a live example, go to friday and try to move a row when in browser mode.
    // it will not work.
    //
    // https://stackoverflow.com/a/13511111
    // https://developer.mozilla.org/en-US/docs/Web/API/Touch_events/Supporting_both_TouchEvent_and_MouseEvent
    private down(e: MouseEvent | TouchEvent) {
        e.preventDefault();
        this.Element.style.cursor = 'grabbing';

        const move = (e: MouseEvent | TouchEvent) => {
            const r = this.revision.Element.getBoundingClientRect();

            const getPosition = (e: MouseEvent | TouchEvent): Point => {
                const m = e as MouseEvent;
                const t = e as TouchEvent;
                const p: Point = {
                    x: 0,
                    y: 0,
                };

                if (!t.touches) {
                    p.x = m.clientX;
                    p.y = m.clientY;
                } else {
                    p.x = t.touches[0].clientX;
                    p.y = t.touches[0].clientY;
                }

                return p;
            };

            const p = getPosition(e);
            const newLeft = p.x - r.left - (this.markerSize / 2);

            if (newLeft >= 0 && newLeft < this.revision.Proof.clientWidth) {
                this.x = newLeft * (this.originalImageWidth / this.revision.Proof.clientWidth);
            }
            const newTop = p.y - r.top - (this.markerSize / 2);
            if (newTop >= 0 && newTop < this.revision.Proof.clientHeight) {
                this.y = newTop * (this.originalImageHeight / this.revision.Proof.clientHeight);
            }

            this.draw();
            this.updateLocalStorage();
        };

        const up = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('touchmove', move);
            window.removeEventListener('mouseup', up);
            window.removeEventListener('touchend', up);
        };

        window.addEventListener('mousemove', move);
        window.addEventListener('touchmove', move);
        window.addEventListener('mouseup', up);
        window.addEventListener('touchend', up);
    }

    private updateLocalStorage() {
        const s = window.localStorage.getItem(this.revision.LocalStorageKey);

        let storage: storage = { markers: [] };
        if (s) {
            storage = JSON.parse(s) as typeof storage;
        }

        storage.markers[this.markerNumber - 1] = {
            position: {
                x: this.x,
                y: this.y,
            },
            notes: this.notes,
        };

        window.localStorage.setItem(this.revision.LocalStorageKey, JSON.stringify(storage));
    }

    Focus() {
        this.Element.style.opacity = '1';
    }

    RemoveFocus() {
        this.Element.style.opacity = '0.5';
    }

    HideMarker() {
        this.Element.style.opacity = '0';
    }
}